/**
 * How long the capture queue keeps resuming itself while the engine is away,
 * and when it stops and tells somebody.
 *
 * An engine outage is weather, not a verdict on the pages, so a wave that fails
 * because the engine went away is put BACK as pending and the drain waits out
 * the restart. That rule is right and is not being reversed here: burning a
 * queued wave on a transient outage is how an operator loses a whole import.
 *
 * It had no end. `capturepoint-main.log` on the reporting machine records a
 * block of five consecutive `capture:process-staged-capture` failures inside one
 * main-process lifetime — 2026-07-31T03:12:38.747Z, 03:13:12.968Z,
 * 03:13:46.722Z, 03:14:20.885Z, 03:14:54.691Z, so 34.2s, 33.8s, 34.2s and 33.8s
 * apart — every one of them reading "Local document processing did not start.
 * The engine was restarted safely; try the folder again." Nothing counted them
 * and nothing bounded them, and while they ran the wave never rested at
 * "failed" — so the screen that says how many times a capture has been tried
 * could not appear, and the operator watched a queue that looked busy and was
 * achieving nothing.
 *
 * WHAT THAT LOG DOES AND DOES NOT PROVE. It proves the CODE had no bound: the
 * drain's catch matched the message, re-queued on sight, and had no term that
 * could ever stop it. It does not prove the loop ran for the whole session, and
 * the comments here used to imply that it did. The same process lifetime (start
 * 03:00:06.524Z, next start 03:15:40.177Z) carries an EARLIER failure at
 * 03:06:18.221Z, 6m20s before the block of five, so the block is a contiguous
 * run and not the lifetime's total — six failures, not five — and the cadence
 * only ran across the last 2m16s of a fifteen-minute session. The log ends 45s
 * before the next process start with nothing in between, so it cannot show how
 * the cycle would have ended. It was going when the operator quit; that is all
 * it says, and it is enough, because an unbounded loop is a defect whether or
 * not this particular operator sat through it.
 *
 * (The log itself is not in this repository — it is on the reporting machine.
 * The timestamps above were re-read against it on review; an earlier revision of
 * this comment mis-transcribed the third as `.712`, which is the
 * `capture:get-engine-health` line 10ms before it.)
 *
 * THE 34-SECOND CADENCE IS HISTORICAL, NOT CURRENT. It is 12s + 20s: the
 * first-progress guard on `process-staged-capture` was `firstProgressTimeoutMs:
 * 12_000` when that build was made. Commit 4849669 ("cold starts are not stalls")
 * raised it to 120_000 — `apps/desktop/src/main/main.ts`, the same call whose
 * failure produces the message quoted above — precisely because the queue
 * ping-ponged between working and failed. So the recorded cadence is a fixture
 * of a build that no longer exists, and every number derived from it must be
 * re-derived against the current guard before it is believed. Under the current
 * guard the drain retries every 140s (120s to fail + a 20s cooldown), which is
 * three automatic resumes inside the patience below, not nine.
 *
 * So the resumption is bounded by TIME rather than by a count of attempts. What
 * is being waited on is one engine restart, and a restart either finishes or it
 * does not. The count is the wrong instrument twice over: two attempts under the
 * old guard was thirty-odd seconds, which is not long enough to conclude
 * anything about a machine, and under the current guard the same two attempts is
 * four and a half minutes. A clock means the same thing in both builds.
 */

/**
 * How long the queue waits between resuming a wave after the engine went away.
 * Long enough for the engine to be killed and come back before the next wave is
 * pushed at it.
 */
export const ENGINE_RESTART_COOLDOWN_MS = 20_000;

/**
 * How long a single outage is waited out before the wave in flight rests at
 * "failed" and the operator is told.
 *
 * Five minutes: the same budget `restartEngineAndTime` in
 * `apps/desktop/src/main/main.ts` gives a restarted engine to report itself
 * healthy before it stops waiting for one. A restart the main process would have
 * given up on is not a restart this wave is going to see finish, and taking the
 * product's own existing number keeps the two from disagreeing about how long a
 * restart may take.
 */
export const ENGINE_OUTAGE_PATIENCE_MS = 5 * 60_000;

/** What the engine says as it goes away under a wave this session is watching. */
const ENGINE_WENT_AWAY = /engine|did not start|restarted safely|stopped unexpectedly/i;

/**
 * The outage currently being waited out. `since` is when it started; `lastSeenAt`
 * is the most recent failure blamed on it, and is what lets a quiet hour end an
 * outage that no longer exists — otherwise a window opened this morning would
 * condemn the first wave of the afternoon on its very first failure.
 *
 * `spent` is the part that makes the bound a bound. WITHOUT IT THE HOLD DOES NOT
 * HOLD. The drain, having given up on a wave, holds the queue for
 * ENGINE_OUTAGE_PATIENCE_MS — and staleness was measured with that same window,
 * so the first failure after every hold was unconditionally at least a full
 * patience past `lastSeenAt`, the outage read as over, and a FRESH one opened
 * with the full five minutes and three more automatic resumes. Against an engine
 * that never comes back that is not a bound at all: it is the reported defect at
 * a 50% duty cycle, restarting for ever. Simulated against the drain's own
 * arithmetic it was 14 automatic resumes an hour, with no end.
 *
 * A wall-clock window strictly longer than the hold would paper over it, but the
 * honest term is not a duration — it is a FACT: this engine was already given
 * everything a restart can take and did not come back. That fact is not undone
 * by time passing, only by the engine answering. So it is carried, and only
 * `clearEngineOutage` (a success, an error the engine itself produced, or a
 * person pressing Retry) puts it down.
 *
 * Null rather than a pair of zeroes: zero is a timestamp, and a record that says
 * "no outage" with the same value an outage could legitimately hold is a rule
 * that silently forgets it was already waiting.
 */
export type EngineOutage = {
  readonly since: number;
  readonly lastSeenAt: number;
  readonly spent: boolean;
} | null;

/** No outage in progress: the engine has answered, or has never been asked. */
export const NO_ENGINE_OUTAGE: EngineOutage = null;

export type OutageVerdict = {
  /** Whether this failure is the engine going away rather than a verdict on the pages. */
  readonly outage: boolean;
  /** Put the wave back as pending and wait out the restart. */
  readonly resume: boolean;
  /** The outage to carry forward. Cleared whenever the engine proves it can answer. */
  readonly outageNow: EngineOutage;
  /**
   * Whether this failure OPENED the outage. The "the queue resumes by itself"
   * notice is true once; said again every thirty seconds for five minutes it is
   * just noise covering a queue that is getting nowhere.
   */
  readonly opensOutage: boolean;
  /**
   * Whether this failure is the one that ran the patience out. The "the engine
   * has not come back" notice belongs to that moment only — every wave condemned
   * after it is the same news, and the operator has already been told.
   */
  readonly spendsPatience: boolean;
};

/**
 * What to do with a wave that has just failed.
 *
 * A failure the engine answered with — a refusal, a document it could not read —
 * closes any outage as a side effect: an engine that produced a real error is an
 * engine that is running.
 */
export const judgeCaptureFailure = (
  message: string,
  outage: EngineOutage,
  now: number,
): OutageVerdict => {
  if (!ENGINE_WENT_AWAY.test(message))
    return {
      outage: false,
      resume: false,
      outageNow: NO_ENGINE_OUTAGE,
      opensOutage: false,
      spendsPatience: false,
    };
  // A spent outage continues regardless of how long ago its last failure was.
  // That is the whole bound: the hold is as long as the patience, so measuring
  // staleness alone would end every spent outage exactly when the queue was let
  // go again, and start the loop over.
  const continuing =
    outage !== null && (outage.spent || now - outage.lastSeenAt < ENGINE_OUTAGE_PATIENCE_MS);
  const since = continuing ? outage!.since : now;
  const alreadySpent = continuing && outage!.spent;
  const resume = !alreadySpent && now - since < ENGINE_OUTAGE_PATIENCE_MS;
  return {
    outage: true,
    resume,
    outageNow: { since, lastSeenAt: now, spent: !resume },
    opensOutage: !continuing,
    spendsPatience: !resume && !alreadySpent,
  };
};

/**
 * WHERE THE OUTAGE LIVES, AND WHY IT IS NOT A COMPONENT REF.
 *
 * An outage is a fact about the ENGINE — one child process, shared by every job
 * and every view in this window — so it is held here, at module scope, and not
 * in `CapturePreview`'s refs where it began. Refs die with the component: an
 * operator who navigates to the jobs list and back, or opens a second job,
 * remounted the view and silently threw away both the outage and the five-minute
 * hold, and the drain marched the queue into the same dead engine on the next
 * tick. The notice this fix prints — "the queue is holding rather than failing
 * the pages behind this one" — is a promise made to a person, and a promise a
 * remount can cancel is not one the renderer is entitled to make.
 *
 * Module scope is the right lifetime for exactly the same reason the engine is:
 * it lasts as long as the window, and a reload starts a genuinely new session
 * where nothing is yet known about the engine.
 */
let currentOutage: EngineOutage = NO_ENGINE_OUTAGE;
let queueHeldUntil = 0;

/** The outage being waited out right now, across every view in this window. */
export const engineOutageNow = (): EngineOutage => currentOutage;

/** Carry the verdict's outage forward. */
export const recordEngineOutage = (outage: EngineOutage): void => {
  currentOutage = outage;
};

/** When the drain may start another wave. Zero once nothing is being waited out. */
export const engineQueueHeldUntil = (): number => queueHeldUntil;

/** Hold the whole queue until `when`, rather than feeding a dead engine. */
export const holdEngineQueueUntil = (when: number): void => {
  queueHeldUntil = when;
};

/**
 * The engine has answered — a wave finished, it produced a real error of its
 * own, or a person looked at the screen and pressed Retry. Whatever was known
 * about the outage is now out of date, including a spent one, and the hold goes
 * with it: holding a queue against an engine that just worked is the opposite
 * defect.
 */
export const clearEngineOutage = (): void => {
  currentOutage = NO_ENGINE_OUTAGE;
  queueHeldUntil = 0;
};
